import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2, Mail } from 'lucide-react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAppStore } from '../store';
import {
  sendMagicLink,
  isMagicLinkUrl,
  completeMagicLinkSignIn,
} from '../services/auth.service';
import type { Invitation } from '../types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const InvitePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { getInvitationByToken, users } = useAppStore();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [magicSubmitting, setMagicSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Load invitation by token
  useEffect(() => {
    const checkInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const inv = await getInvitationByToken(token);
        if (!inv) {
          setError('Diese Einladung ist ungültig oder wurde zurückgezogen.');
        } else if (inv.status !== 'pending') {
          setError('Diese Einladung wurde bereits verwendet.');
        } else if (new Date() > inv.expiresAt) {
          setError('Diese Einladung ist abgelaufen.');
        } else {
          setInvitation(inv);
        }
      } catch {
        setError('Einladung konnte nicht geladen werden.');
      }
      setLoading(false);
    };

    checkInvitation();
  }, [token, getInvitationByToken]);

  // Detect magic link return URL and complete sign-in. AuthContext will then
  // create the pending user from the invitation.
  useEffect(() => {
    if (!isMagicLinkUrl()) return;

    const run = async () => {
      setCompleting(true);
      try {
        await completeMagicLinkSignIn();
        // AuthContext handles user creation + "pending approval" alert and sign-out.
      } catch (err: any) {
        if (err?.message === 'MISSING_EMAIL') {
          const entered = window.prompt(
            'Bitte gib zur Bestätigung deine E-Mail-Adresse ein:'
          );
          if (entered) {
            try {
              await completeMagicLinkSignIn(entered);
            } catch (retryErr: any) {
              setError(retryErr?.message || 'Anmeldung fehlgeschlagen.');
            }
          } else {
            setError('Anmeldung abgebrochen.');
          }
        } else {
          setError(err?.message || 'Anmeldung fehlgeschlagen.');
        }
      } finally {
        setCompleting(false);
        // Strip Firebase auth params from the URL so refreshes don't re-trigger
        if (token) {
          window.history.replaceState({}, document.title, `/invite/${token}`);
        }
      }
    };

    run();
  }, [token]);

  const handleAcceptWithGoogle = async () => {
    if (!invitation) return;

    setAccepting(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (!user.email) {
        setError('Google-Konto muss eine E-Mail-Adresse haben.');
        await auth.signOut();
        setAccepting(false);
        return;
      }

      if (user.email.toLowerCase() !== invitation.email.toLowerCase()) {
        setError(`Diese Einladung ist für ${invitation.email}. Bitte melde dich mit diesem Konto an.`);
        await auth.signOut();
        setAccepting(false);
        return;
      }

      // AuthContext picks up the auth state change, finds the pending invite
      // by email, creates the user with status=pending, and signs them out.
      // No further action needed here.
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Anmeldung abgebrochen.');
      } else {
        console.error('Error accepting invitation:', err);
        setError(err.message || 'Einladung konnte nicht angenommen werden.');
      }
      setAccepting(false);
    }
  };

  const handleAcceptWithMagicLink = async () => {
    if (!invitation || !token) return;

    setMagicSubmitting(true);
    setError(null);

    try {
      const continueUrl = `${window.location.origin}/invite/${token}`;
      await sendMagicLink(invitation.email, continueUrl);
      setMagicSent(true);
    } catch (err: any) {
      setError(err?.message || 'Link konnte nicht gesendet werden.');
    } finally {
      setMagicSubmitting(false);
    }
  };

  const getInviterName = () => {
    if (!invitation) return 'Jemand';
    const inviter = users.find(u => u.id === invitation.invitedBy);
    return inviter?.name || 'Ein Familienmitglied';
  };

  const roleLabel = (role: Invitation['role']) => {
    if (role === 'family') return 'Eigentümer';
    if (role === 'familyMember') return 'Familienmitglied';
    return 'Gast';
  };

  if (loading || completing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            {completing && (
              <p className="text-sm text-muted-foreground">Anmeldung wird abgeschlossen…</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Einladungsfehler</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/login')}>
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (magicSent && invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-6">
              <Mail className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">E-Mail gesendet</h2>
            <p className="text-muted-foreground mb-2">
              Wir haben einen Anmelde-Link an
            </p>
            <p className="font-medium mb-6">{invitation.email}</p>
            <p className="text-sm text-muted-foreground mb-6">
              Klicke auf den Link in der E-Mail, um deine Registrierung abzuschließen.
              Danach prüft der Admin deinen Zugang.
            </p>
            <Button variant="outline" onClick={() => setMagicSent(false)}>
              Zurück
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Du bist eingeladen!</CardTitle>
          <CardDescription>
            {getInviterName()} hat dich zu Holiday Homes der Familie Richner eingeladen als{' '}
            <span className="font-semibold text-foreground">
              {roleLabel(invitation.role)}
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-muted rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Diese Einladung ist für</p>
            <p className="font-medium">{invitation.email}</p>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              Läuft ab am {invitation.expiresAt.toLocaleDateString()}
            </span>
          </div>

          <div className="space-y-3">
            <Button
              className="w-full"
              size="lg"
              onClick={handleAcceptWithGoogle}
              disabled={accepting || magicSubmitting}
            >
              {accepting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Anmelden…
                </>
              ) : (
                'Mit Google annehmen'
              )}
            </Button>

            <Button
              className="w-full"
              variant="outline"
              size="lg"
              onClick={handleAcceptWithMagicLink}
              disabled={accepting || magicSubmitting}
            >
              {magicSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Link wird gesendet…
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  E-Mail-Link senden
                </>
              )}
            </Button>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Nach der Registrierung prüft der Admin deinen Zugang. Du wirst
              nach der Freigabe benachrichtigt.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitePage;
